import { logger } from '@/logging';
import { NotificationCategory, SystemNotification, useNotificationStore } from '@/store/useNotificationStore';
import { usePipelineEventStore } from '@/store/usePipelineEventStore';
import { useRunStore } from '@/store/useRunStore';
import { Run } from '@/types';
// Note: SSEEventWrapper type is defined in @/types for reference but the API unwraps it before sending

class SSEService {
  private eventSource: EventSource | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  // Connection state callbacks
  onConnect?: () => void;
  onDisconnect?: () => void;

  connect() {
    this.disconnect();

    const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
    const url = `${baseUrl}/events/stream`;

    logger.info('🔌 SSE Service: Attempting to connect (NO AUTH)', {
      baseUrl,
      url,
      hasViteApiBaseUrl: !!import.meta.env.VITE_API_BASE_URL,
      viteApiBaseUrl: import.meta.env.VITE_API_BASE_URL,
      withCredentials: true,
      authBypassed: true,
    });

    try {
      this.eventSource = new EventSource(url, { withCredentials: true });

      logger.info('✅ SSE Service: EventSource created successfully (NO AUTH)', {
        url,
        readyState: this.eventSource.readyState,
        readyStates: {
          CONNECTING: EventSource.CONNECTING,
          OPEN: EventSource.OPEN,
          CLOSED: EventSource.CLOSED
        },
        authBypassed: true
      });
    } catch (error) {
      logger.error('❌ SSE Service: Failed to create EventSource', {
        error: error instanceof Error ? error.message : 'Unknown error',
        url,
        baseUrl,
        authBypassed: true
      });
      return;
    }

    // Handle run status snapshots - direct RunStatus updates, no API call needed!
    // Note: The API unwraps SSEEventWrapper and sends the payload directly
    this.eventSource.addEventListener('run_status', (e: MessageEvent) => {
      try {
        // The data is the payload from SSEEventWrapper, not the wrapper itself
        const runStatus: Run = JSON.parse(e.data);

        // Direct update to store with complete RunStatus from SSE
        useRunStore.getState().updateRun(runStatus);

        // Extract pipeline events from stage counts for Module Activity Timeline
        const eventStore = usePipelineEventStore.getState();
        const fallbackTimestamp = Date.now();

        // Debug logging
        console.log('Processing run status for events', {
          runId: runStatus.id,
          hasStageCounts: !!runStatus.stageCounts,
          stageCountKeys: runStatus.stageCounts ? Object.keys(runStatus.stageCounts) : [],
          stageCounts: runStatus.stageCounts,
        });

        // If the run has stageCounts, create events from active stages
        if (runStatus.stageCounts) {
          Object.entries(runStatus.stageCounts).forEach(([stage, counts]) => {
            // Only create events for stages that are currently active (processing or queued)
            const isCurrentlyActive = (
              (counts as any).queued > 0 ||
              (counts as any).processing > 0
            );

            console.log('Processing stage', {
              stage,
              counts,
              isCurrentlyActive,
              queued: (counts as any).queued,
              processing: (counts as any).processing,
              complete: (counts as any).complete,
              failed: (counts as any).failed,
              totalProcessed: (counts as any).totalProcessed,
            });

            if (isCurrentlyActive) {
              // Map stage names to module names (handle both lowercase and capitalized)
              let moduleName = stage;
              const stageLower = stage.toLowerCase();
              if (stageLower === 'convert' || stageLower === 'pdf') {
                moduleName = 'worker_convert';
              } else if (stageLower === 'chunk') {
                moduleName = 'worker_chunk';
              } else if (stageLower === 'translate') {
                moduleName = 'worker_translate';
              } else if (stageLower === 'enrich') {
                moduleName = 'worker_enrich';
              } else if (stageLower === 'summary') {
                moduleName = 'worker_summary';
              } else if (stageLower === 'index') {
                moduleName = 'worker_index';
              } else if (stageLower.includes('discover')) {
                moduleName = runStatus.connectorId?.includes('graph') ? 'connector_graph' : 'connector_blob';
              }

              console.log('Mapped stage to module', {
                stage,
                stageLower,
                moduleName,
              });

              // Create a single event for this currently active stage
              // Use lastProcessingTime if available and items are processing
              let eventTimestamp = fallbackTimestamp;
              if ((counts as any).processing > 0 && (counts as any).lastProcessingTime) {
                eventTimestamp = new Date((counts as any).lastProcessingTime).getTime();
              }

              eventStore.addEvent({
                module: moduleName,
                timestamp: eventTimestamp,
                type: 'processing' as const,
                runId: runStatus.id,
                status: 'processing',
              });

              logger.debug('Added active stage event', {
                module: moduleName,
                stage,
                timestamp: eventTimestamp,
                processingCount: (counts as any).processing,
                queuedCount: (counts as any).queued,
              });
            }
          });
        }

        // Add orchestrator status event if run is active
        if (runStatus.status === 'running' || runStatus.status === 'processing') {
          eventStore.addEvent({
            module: 'orchestrator',
            timestamp: fallbackTimestamp,
            type: 'status',
            runId: runStatus.id,
            status: runStatus.status,
          });
        }

        logger.debug('Real-time run update received', {
          runId: runStatus.id,
          status: runStatus.status,
          eventSource: 'sse',
          correlationId: e.lastEventId, // The API sets this as the event ID
        });
      } catch (error) {
        logger.warn('Failed to process run_status SSE event', {
          error: error instanceof Error ? error.message : 'Unknown error',
          data: e.data,
        });
      }
    });

    // Handle system notifications (health, errors, alerts)
    // Note: The API unwraps SSEEventWrapper and sends the payload directly
    this.eventSource.addEventListener('system_notification', (e: MessageEvent) => {
      try {
        // The data is the payload from SSEEventWrapper, not the wrapper itself
        const notification: SystemNotification = JSON.parse(e.data);

        // Validate required fields
        if (!notification.severity || !notification.category || !notification.source) {
          logger.warn('System notification missing required fields', {
            notification,
            missingSeverity: !notification.severity,
            missingCategory: !notification.category,
            missingSource: !notification.source,
          });
          return;
        }

        logger.debug('System notification received', {
          severity: notification.severity,
          category: notification.category,
          title: notification.title,
          source: notification.source,
        });

        const notificationStore = useNotificationStore.getState();

        // Route notification based on category with error handling for each update
        switch (notification.category) {
          case NotificationCategory.WORKER:
            try {
              notificationStore.updateWorkerHealth(notification);
            } catch (error) {
              logger.error('Failed to update worker health', {
                error: error instanceof Error ? error.message : 'Unknown error',
                notification,
              });
            }
            break;
          case NotificationCategory.HEALTH:
            try {
              notificationStore.updateServiceHealth(notification);
            } catch (error) {
              logger.error('Failed to update service health', {
                error: error instanceof Error ? error.message : 'Unknown error',
                notification,
              });
            }
            break;
          case NotificationCategory.SYSTEM:
            try {
              // Only update system metrics if the notification contains metrics data
              if (notification.metrics) {
                notificationStore.updateSystemMetrics(notification);
              }
              // Note: SYSTEM notifications will also be added to general notification list below
            } catch (error) {
              logger.error('Failed to update system metrics', {
                error: error instanceof Error ? error.message : 'Unknown error',
                notification,
              });
            }
            break;
          default:
            logger.debug('Unknown notification category', {
              category: notification.category,
              notification,
            });
        }

        // Add all system notifications to store (excluding health/worker which are handled separately)
        if (notification.category !== NotificationCategory.HEALTH &&
            notification.category !== NotificationCategory.WORKER) {
          try {
            notificationStore.addNotification(notification);
          } catch (error) {
            logger.error('Failed to add notification to store', {
              error: error instanceof Error ? error.message : 'Unknown error',
              notification,
            });
          }
        }

      } catch (error) {
        logger.warn('Failed to process system_notification SSE event', {
          error: error instanceof Error ? error.message : 'Unknown error',
          data: e.data,
        });
      }
    });

    this.eventSource.addEventListener('error', (e) => {
      this.reconnectAttempts++;
      logger.warn('⚠️ SSE connection error', {
        attempt: this.reconnectAttempts,
        maxAttempts: this.maxReconnectAttempts,
        readyState: this.eventSource?.readyState,
        url,
        errorEvent: {
          type: e.type,
          target: e.target,
        },
      });

      this.onDisconnect?.();

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        logger.error('❌ Max SSE reconnection attempts reached', {
          attempts: this.reconnectAttempts,
          url,
        });
        this.disconnect();
      } else {
        logger.info('🔄 SSE will attempt reconnection', {
          nextAttempt: this.reconnectAttempts + 1,
          maxAttempts: this.maxReconnectAttempts,
        });
      }
    });

    this.eventSource.onopen = async () => {
      this.reconnectAttempts = 0;
      logger.info('🎉 SSE connection established successfully', {
        url,
        readyState: this.eventSource?.readyState,
        timestamp: new Date().toISOString(),
      });

      // Initialize notification store with initial data if not already done
      const notificationStore = useNotificationStore.getState();
      if (!notificationStore.isInitialized) {
        await notificationStore.initializeFromAPI();
      }

      this.onConnect?.();
    };
  }

  disconnect() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }
}

export const sseService = new SSEService();
